import moment from "moment";
import React from "react";
import { Badge } from "react-bootstrap";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import PageTitle from "../layouts/PageTitle";
import {
  getUsersAssesmentDraftMinified,
  getUserAccessibleTemplates,
} from "../../services/DraftService";
import { getCompletedAssessmentsMinified } from "../../services/AssesmentService";
import { canImpersonate, getViewAsUser } from "../../services/ViewAsService";
import { BACKEND_URL } from "../../baseUrl";

const TEAL = "#0d5c4a";

// Collapsible section wrapper (expand/collapse card)
const Section = ({ title, count, defaultOpen = true, children }) => {
  const [open, setOpen] = React.useState(defaultOpen);
  return (
    <div className="card">
      <div
        className="card-header"
        style={{ cursor: "pointer" }}
        onClick={() => setOpen((o) => !o)}
      >
        <h4 className="card-title mb-0">
          {title}
          {count !== undefined && (
            <Badge variant="light" className="ms-2">
              {count}
            </Badge>
          )}
        </h4>
        <i className={`fa fa-chevron-${open ? "up" : "down"}`} />
      </div>
      {open && <div className="card-body">{children}</div>}
    </div>
  );
};

// Card that mirrors the end-user assessment picker (teal card, image if available)
const TemplateCard = ({ template, roleAssigned }) => {
  const [imgOk, setImgOk] = React.useState(!!template.image);
  const imgUrl = template.image
    ? `${BACKEND_URL}/public/${template.image}`
    : null;
  return (
    <div
      style={{
        borderRadius: "10px",
        overflow: "hidden",
        background: TEAL,
        boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: "90px",
      }}
    >
      {imgOk && imgUrl && (
        <img
          src={imgUrl}
          alt={template.name}
          onError={() => setImgOk(false)}
          style={{ width: "100%", height: "150px", objectFit: "cover" }}
        />
      )}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "14px 16px",
          flexGrow: 1,
        }}
      >
        <span style={{ color: "#fff", fontWeight: 600, lineHeight: 1.25 }}>
          {template.name}
          {roleAssigned && (
            <Badge variant="info" className="ms-2" style={{ fontWeight: 400 }}>
              by role
            </Badge>
          )}
        </span>
        <span
          style={{
            flexShrink: 0,
            marginLeft: "10px",
            width: "30px",
            height: "30px",
            borderRadius: "50%",
            background: "#fff",
            color: TEAL,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <i className="fa fa-chevron-right" />
        </span>
      </div>
    </div>
  );
};

const ownerName = (item) =>
  (item.assesmentUser && item.assesmentUser.name) || "";
const location = (item) =>
  [item.premisesName, item.city].filter((v) => v && v !== "-").join(", ") || "-";

// Rich table matching the desktop: owner name, template, country, location, date
const AssessmentTable = ({ items, fallbackName }) =>
  items.length > 0 ? (
    <div className="table-responsive">
      <table className="table display">
        <thead>
          <tr>
            <th>Name</th>
            <th>Template</th>
            <th>Country</th>
            <th>Location</th>
            <th>Date</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.id || item._id}>
              <td>{ownerName(item) || fallbackName || "-"}</td>
              <td>
                {(item.assessmentTemplate && item.assessmentTemplate.name) ||
                  "-"}
              </td>
              <td>{item.country || "-"}</td>
              <td>{location(item)}</td>
              <td>
                {item.assessmentDate
                  ? moment(item.assessmentDate).format("DD-MM-YYYY")
                  : "-"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  ) : (
    "No assessments"
  );

export const ViewAsAssessments = () => {
  const authUser = useSelector((state) => state.auth.auth);
  const viewAsUser = canImpersonate(authUser) ? getViewAsUser() : null;
  const [drafts, setDrafts] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [completedList, setCompletedList] = React.useState([]);
  const [templates, setTemplates] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [search, setSearch] = React.useState("");

  React.useEffect(() => {
    if (!viewAsUser) return;
    setLoading(true);
    Promise.all([
      // Completed comes from the assessments collection (same source the
      // desktop uses); ?viewAs is added by the axios interceptor so the
      // backend scopes it to the impersonated user's hierarchy.
      getCompletedAssessmentsMinified(viewAsUser.id).catch(() => []),
      // Drafts (mobile in-progress saves) — used only for the In Progress list.
      getUsersAssesmentDraftMinified(viewAsUser.id).catch(() => ({
        results: [],
        total: 0,
      })),
      getUserAccessibleTemplates(viewAsUser.id).catch(() => []),
    ])
      .then(([completedData, draftData, tplData]) => {
        setCompletedList(
          (Array.isArray(completedData) ? completedData : []).map((x) => ({
            ...x,
            isCompleted: true,
          }))
        );
        setDrafts(draftData.results || []);
        setTotal(draftData.total || 0);
        // dedupe by template id — same template can appear for user + supervisees
        const seen = new Set();
        const uniq = [];
        (Array.isArray(tplData) ? tplData : []).forEach((a) => {
          const t = a.assessmentTemplate;
          const key = t && (t._id || t.id);
          if (!t || !key || seen.has(String(key))) return;
          seen.add(String(key));
          uniq.push({ template: t, roleAssigned: !!a.roleAssigned });
        });
        setTemplates(uniq);
      })
      .catch(() => setError("Could not load this user's assessments."))
      .finally(() => setLoading(false));
  }, []);

  if (!viewAsUser) {
    return (
      <div className="alert alert-info">
        This page is only available in "View as" mode. Go to the{" "}
        <Link to="/users">Users page</Link> and click the eye icon on a user.
      </div>
    );
  }

  // In-progress = unfinished drafts; Completed = finished assessments
  const inProgress = drafts.filter((d) => !d.isCompleted);
  const q = search.trim().toLowerCase();
  const completed = q
    ? completedList.filter((a) => {
        const hay = [
          ownerName(a),
          a.assessmentTemplate && a.assessmentTemplate.name,
          a.country,
          a.premisesName,
          a.city,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return hay.includes(q);
      })
    : completedList;

  return (
    <>
      <PageTitle
        activeMenu={`Assessments of ${viewAsUser.name}`}
        motherMenu="View as"
      />
      {loading && <div className="alert alert-light">Loading…</div>}
      {error && <div className="alert alert-danger">{error}</div>}
      {!loading && !error && (
        <>
          <Section
            title="Available Assessments (what this user can start)"
            count={templates.length}
          >
            {templates.length > 0 ? (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns:
                    "repeat(auto-fill, minmax(300px, 1fr))",
                  gap: "20px",
                }}
              >
                {templates.map((t) => (
                  <TemplateCard
                    key={t.template._id || t.template.id}
                    template={t.template}
                    roleAssigned={t.roleAssigned}
                  />
                ))}
              </div>
            ) : (
              "This user has no assessment templates available."
            )}
          </Section>

          <Section title="In Progress" count={inProgress.length}>
            {total > drafts.length && (
              <div className="alert alert-info">
                Showing the {drafts.length} most recent of {total} saved
                assessments.
              </div>
            )}
            <AssessmentTable
              items={inProgress}
              fallbackName={viewAsUser.name}
            />
          </Section>

          <Section title="Completed" count={completedList.length}>
            <input
              type="text"
              className="form-control mb-3"
              placeholder="Search by name, template, country or location…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {q && (
              <div className="mb-2 text-muted">
                {completed.length} of {completedList.length} match "{search}"
              </div>
            )}
            <AssessmentTable items={completed} fallbackName={viewAsUser.name} />
          </Section>
        </>
      )}
    </>
  );
};

export default ViewAsAssessments;
